import { TextOkthxLink } from "@/components/connectors/textMessages/TextOkthxLink.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { TEXT_MESSAGES_NAME, textMessagesNotice } from "@/shared/textMessages.ts";
import { useGateValue } from "@statsig/react-bindings";
import { AlertCircle, X } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

export const AccountReauthBanner = () => {
  const { userContext } = useCurrentUserContext();
  const textMessagesEnabled = useGateValue("text_messages_connector");
  // Dismissal is keyed to the exact set of expired connectors, not a bare
  // boolean, so dismissing one stale warning doesn't swallow the next: if a
  // different account expires later, the key changes and the banner returns.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  // In a WebView, the native app shows its own banner
  if (window.isWebView) return null;

  const needingReauth = userContext?.user.connectors?.filter((a) => a.needsAuth) ?? [];
  const textMessages = textMessagesEnabled ? userContext?.user.textMessages : undefined;
  const textReason = textMessages?.needsAttention ?? null;
  if (needingReauth.length === 0 && textReason === null) return null;

  // The text reason is part of the key, so a dismissed reply-limit notice
  // comes back if the person opts out instead.
  const reauthKey = [
    ...needingReauth.map((a) => a.id).sort((a, b) => a - b),
    ...(textReason ? [`text:${textReason}`] : []),
  ].join(",");
  if (reauthKey === dismissedKey) return null;

  const dismissButton = (
    // Dismissal lasts the session only, deliberately: the account really is
    // broken until it's reconnected, and a dismissal that outlived a reload
    // would hide a live problem indefinitely.
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={
        needingReauth.length === 0 ? "Dismiss texting warning" : "Dismiss reconnect warning"
      }
      onClick={() => setDismissedKey(reauthKey)}
      className="absolute right-2 top-1/2 h-auto -translate-y-1/2 p-1 text-amber-700 hover:bg-amber-200 hover:text-amber-700"
      data-statsig-name="account-reauth-banner-dismiss"
    >
      <X className="size-4" />
    </Button>
  );

  // Only Text Messages needs attention: its fix is a text to Okthx, not a
  // reconnect, so the banner says why and links Okthx's number to text.
  if (needingReauth.length === 0 && textMessages && textReason) {
    return (
      <div className="relative flex items-center justify-center gap-2 bg-amber-100 py-2 pl-4 pr-10 text-sm text-amber-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>
          {textMessagesNotice(textReason).message}{" "}
          <TextOkthxLink
            reason={textReason}
            assistantNumber={textMessages.assistantNumber}
            className="font-medium underline"
            statsigName="account-reauth-banner-text-okthx"
          />
        </span>
        {dismissButton}
      </div>
    );
  }

  // Name the connector(s) so the user knows what to fix without hunting
  // through Settings. Dedupe: multiple connectors can share a display name
  // (e.g. two Google accounts) and repeating it reads like a bug.
  const names = [
    ...new Set([
      ...needingReauth.map((a) => a.displayName.trim()).filter(Boolean),
      ...(textReason ? [TEXT_MESSAGES_NAME] : []),
    ]),
  ];
  const subject =
    names.length > 0
      ? new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(names)
      : "One of your accounts";
  const verb = names.length > 1 ? "need" : "needs";

  // The two Reconnect actions live on different Settings panels: the login
  // account's sits in the Account panel's profile header, secondary accounts'
  // Connectors is a nav destination, so link that rather than the settings
  // copy: both render the same gallery panel and its per-account Reconnect
  // rows, but only the nav one backs out to a surface that lists it. Account
  // is still a settings tab and keeps its tab path. When both need reauth,
  // Account wins — the login credential is the one the rest of the app
  // depends on.
  const target = needingReauth.some((a) => a.isLogin) ? "/settings/account" : "/connectors";

  return (
    <div className="relative flex items-center justify-center gap-2 bg-amber-100 py-2 pl-4 pr-10 text-sm text-amber-700">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span>
        {subject} {verb} to be reconnected.{" "}
        <Link to={target} className="underline font-medium">
          Go to Settings
        </Link>
      </span>
      {dismissButton}
    </div>
  );
};
